import { useState, type MouseEvent } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type TaskType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';

export const canRemindTask = (task: TaskType, userId: number): boolean => {
    if (!userId || Number(task.taskDeleted)) return false;
    if (userId !== Number(task.taskController)) return false;
    const status = Number(task.taskStatus);
    return status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK;
};

interface TaskRemindButtonProps {
    task: TaskType;
    variant?: 'table' | 'kanban';
    className?: string;
}

const TaskRemindButton = ({ task, variant = 'table', className }: TaskRemindButtonProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [busy, setBusy] = useState(false);

    const userId = Number(auth?.user?.ID || 0);
    if (!canRemindTask(task, userId)) return null;

    const handleClick = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;

        setBusy(true);
        const result = await apiTask.remindTaskExecutor(task.ID);
        setBusy(false);

        if (result?.stat) {
            showToast(t('messRemindSent'), 'success');
            return;
        }

        if (result?.errCode === 'NO_TELEGRAM') {
            showToast(t('errRemindNoTelegram'), 'warning');
            return;
        }
        if (result?.errCode === 'SEND_FAILED') {
            showToast(t('errRemindSendFailed'), 'danger');
            return;
        }
        if (result?.errCode === 'FORBIDDEN') {
            showToast(t('errRemindForbidden'), 'warning');
            return;
        }
        showToast(t('common:messError'), 'danger');
    };

    return (
        <EToolTip title={t('btnRemind')}>
            <Button
                type="button"
                variant="outline-secondary"
                className={[
                    'task-remind-btn',
                    variant === 'kanban' ? 'task-remind-btn--kanban' : 'task-remind-btn--table',
                    className,
                ]
                    .filter(Boolean)
                    .join(' ')}
                disabled={busy}
                aria-label={t('btnRemind')}
                onClick={(e) => void handleClick(e)}
                onKeyDown={(e) => e.stopPropagation()}
            >
                {busy ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                    <SolarIcon name="bell" />
                )}
            </Button>
        </EToolTip>
    );
};

export default TaskRemindButton;
